import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Calculator, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectInquiryPopup({ isVisible, onClose, calculatorData }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    project_type: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/project-inquiry`, {
        ...formData,
        calculator_data: calculatorData,
        source: 'comparison_tool_popup'
      });
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when popup opens
  useEffect(() => {
    if (isVisible) {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        project_type: '',
        notes: ''
      });
    }
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-charcoal/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-charcoal border border-gold/30 w-full max-w-lg relative" data-testid="project-inquiry-popup">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-stone/50 hover:text-stone transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>

              {isSubmitted ? (
                /* Success State */
                <div className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                  >
                    <CheckCircle size={64} className="text-gold mx-auto mb-6" />
                  </motion.div>
                  <h3 className="heading-section text-2xl text-stone mb-4">We'll Be In Touch!</h3>
                  <p className="text-stone/60">
                    Our team will review your project details and reach out within 24 hours 
                    with a customized analysis.
                  </p>
                </div>
              ) : (
                /* Form */
                <div className="p-8">
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-gold/10 border border-gold/30">
                      <Calculator size={20} className="text-gold" />
                    </div>
                    <span className="tech-label">//PROJECT_INQUIRY</span>
                  </div>
                  
                  <h3 className="heading-section text-2xl text-stone mb-2">
                    Get a Custom <span className="text-gold">Analysis</span>
                  </h3>
                  <p className="text-stone/50 text-sm mb-6">
                    Interested in the savings? Let us provide a detailed ROI analysis 
                    tailored to your specific project.
                  </p>

                  {/* Savings Preview */}
                  {calculatorData && (
                    <div className="bg-gold/10 border border-gold/20 p-4 mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-stone/60 text-sm">Your Estimated Savings</span>
                        <span className="heading-display text-2xl text-gold">
                          ${Math.abs(calculatorData.savings || 0).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-stone/40 text-xs mt-1">
                        Based on {calculatorData.quantity || 0} pieces × {calculatorData.length || 0}m
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-stone text-xs mb-1">Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="input text-sm"
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <label className="block text-stone text-xs mb-1">
                          Email <span className="text-gold">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="input text-sm"
                          placeholder="john@company.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-stone text-xs mb-1">Company</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="input text-sm"
                          placeholder="Company name"
                        />
                      </div>
                      <div>
                        <label className="block text-stone text-xs mb-1">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="input text-sm"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-stone text-xs mb-1">Project Type</label>
                      <select
                        name="project_type"
                        value={formData.project_type}
                        onChange={handleChange}
                        className="input text-sm"
                      >
                        <option value="">Select project type...</option>
                        <option value="marine_coastal">Marine / Coastal</option>
                        <option value="parking_structure">Parking Structure</option>
                        <option value="bridge_highway">Bridge / Highway</option>
                        <option value="water_wastewater">Water / Wastewater</option>
                        <option value="healthcare_mri">Healthcare / MRI</option>
                        <option value="industrial">Industrial</option>
                        <option value="commercial">Commercial Building</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-stone text-xs mb-1">Additional Notes</label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="input text-sm resize-none"
                        rows={2}
                        placeholder="Tell us about your project..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-primary w-full"
                      data-testid="submit-inquiry"
                    >
                      {isSubmitting ? 'Sending...' : 'Get My Custom Analysis'}
                      <Send size={16} className="ml-2" />
                    </button>

                    <p className="text-stone/30 text-xs text-center">
                      We'll respond within 24 hours. No spam, ever.
                    </p>
                  </form>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
